/**
 * The home api-"client".
 */

import { getApplicationLanguage } from 'flow/utils/browser-utils';
import { getClientType } from 'flow/utils/app-utils';
import isElectron from 'is-electron';
import { addHomeLogEntry } from 'flow/utils/browser-utils';
import appConfig from 'app-config';
import { configure } from '@telavox/api-internal/home-client';

const SAME_SITE_FILTER_HEADER = {
    header: 'X-TVX-EMBEDDED-APPLICATION-MUMBO-JUMBO-STRING',
    value: '12dbc4217f21234a15aded1252921070',
};
const WEBAPP_TTL_HEADER = { header: 'X-TVX-WEBAPP-TTL', value: 'true' };

// We use the default host to do the first fetch for the brandingDTO.
const isLocalhost = window.location.hostname.includes('localhost');
const apiPath = '/api/internal';
const defaultApiHost = isLocalhost ? '' : appConfig.apiUrl;

const defaultApiEndpoint = defaultApiHost + apiPath;
const isGeonexConfigured = appConfig?.isGeonexConfigured;

const HomeClient = {
    /**
     * The API host and endpoint.
     * The API-host is used to form the paths used in requests to the old API,
     * and the endpoint is used for requests to the new API.
     */
    apiPath,
    apiHost: defaultApiHost, // New value is set in brandingV2-utils
    endpoint: defaultApiEndpoint, // New value is set in brandingV2-utils
    devApiPath: null,

    basic: null,

    regionHostEndpoint: null,

    authToken: null,

    accessToken: null,

    additionalHeaders: [],

    clientTypeOverride: null,

    getActiveEndpoint: function () {
        return this.regionHostEndpoint || this.endpoint;
    },

    updateDefaultApiHost: function (newHost) {
        if (!newHost) return;

        if (!isLocalhost) {
            this.apiHost = newHost;
            this.regionHostEndpoint = newHost + this.apiPath;

            if (isElectron()) {
                const { ipcRenderer } = window.require('electron');
                ipcRenderer.send('update-cookie-api-endpoint', newHost);
            }
        } else {
            this.devApiPath = newHost;
        }
    },

    addAuthServiceToken: function (authToken) {
        const authBearerValue = `Bearer ${authToken}`;
        const authExistingHeader = this.additionalHeaders.find(
            (header) => header.header === 'Authorization',
        );
        if (authExistingHeader) {
            authExistingHeader.value = authBearerValue;
        } else {
            this.additionalHeaders.push({
                header: 'Authorization',
                value: authBearerValue,
            });
        }
    },

    removeAuthServiceToken: function () {
        this.additionalHeaders = this.additionalHeaders.filter(
            (hd) =>
                !(hd.header === 'Authorization' && hd.value.includes('Bearer')),
        );
    },

    installSameSiteHeader: function () {
        if (this.additionalHeaders.indexOf(SAME_SITE_FILTER_HEADER) === -1) {
            this.additionalHeaders.push(SAME_SITE_FILTER_HEADER);
        } else {
            console.warn(
                'Attempted to install same site header when it was already installed.',
            );
        }
    },

    installWebappTtlHeader: function () {
        if (this.additionalHeaders.indexOf(WEBAPP_TTL_HEADER) === -1) {
            this.additionalHeaders.push(WEBAPP_TTL_HEADER);
        } else {
            console.warn(
                'Attempted to install webapp TTL header when it was already installed.',
            );
        }
    },

    getTvxClientType: function () {
        return this.clientTypeOverride
            ? this.clientTypeOverride
            : getClientType(isElectron());
    },

    overrideTvxClientType: function (type) {
        this.clientTypeOverride = type;
    },

    // Default "Not authorized"-handler
    notAuthorizedHandler: function (request) {
        throw new Error('Request was not authorized: ' + request);
    },

    requestNotifier: {
        onStart: function () {},
        onStop: function () {},
    },

    verifyUser: function () {
        return true;
    },

    /**
     * Create the request.
     */
    createRequest: function () {
        if (window.XMLHttpRequest) {
            return new window.XMLHttpRequest();
        } else if (window.ActiveXObject) {
            return new window.ActiveXObject('Microsoft.XMLHTTP');
        } else {
            throw new Error('Unable to construct request');
        }
    },

    /**
     * Construct a HTTP request to the specified path and return the result in callback's:
     * onResult- and onError-methods.
     *
     * @param callback the callback object (must implement: 'onResult' and 'onError')
     * @param conf = {
     *   method: <METHOD>,
     *   path: <PATH>,
     *   params: [<PARAM {key: value}>]
     *   headers: [<HEADER {key: value}>]
     *   data: <DATA>
     * }
     *
     */
    exec: function (callback, conf) {
        const req = this.createRequest();
        let query = '';
        let sendTime = null;

        if (conf.params) {
            for (const prop in conf.params) {
                if (conf.params[prop]) {
                    if (
                        conf.method === 'GET' &&
                        Array.isArray(conf.params[prop])
                    ) {
                        query = conf.params[prop].reduce(
                            (acc, item) => acc + '&' + prop + '=' + item,
                            query,
                        );
                    } else {
                        query += '&' + prop + '=' + conf.params[prop];
                    }
                }
            }

            if (query !== '') {
                query = '?' + query.substring(1);
            }
        }

        req.open(
            conf.method,
            this.getActiveEndpoint() + conf.path + query,
            true,
        );
        req.withCredentials = true;

        if (isGeonexConfigured && this.devApiPath && isLocalhost) {
            req.setRequestHeader('X-Target-Host', this.devApiPath);
        }

        this.additionalHeaders.forEach(({ header, value }) =>
            req.setRequestHeader(header, value),
        );

        if (this.basic != null) {
            req.setRequestHeader('Authorization', 'Basic ' + this.basic);
            this.basic = null;
        }

        if (this.accessToken != null) {
            req.setRequestHeader('Authorization', 'Bearer ' + this.accessToken);
            this.accessToken = null;
        }

        if (this.authToken != null) {
            req.setRequestHeader('X-TVX-Auth-Token', this.authToken);
            this.authToken = null;
        }

        req.setRequestHeader('X-TVXClientType', this.getTvxClientType());
        req.setRequestHeader('X-TVX-WEBAPP-VERSION', appConfig?.version);

        if (conf.headers) {
            for (const prop in conf.headers) {
                req.setRequestHeader(prop, conf.headers[prop]);
            }
        }

        const applicationLanguage = getApplicationLanguage();
        if (applicationLanguage) {
            req.setRequestHeader('Accept-Language', applicationLanguage);
        }

        const outerStack = new Error().stack;

        req.onreadystatechange = (function (
            notifier,
            notAuthorized,
            verifyUser,
        ) {
            return () => {
                try {
                    if (req.readyState === 4) {
                        addHomeLogEntry(
                            `${conf.method} ${req.status} ${req.statusText} ${
                                Date.now() - sendTime
                            }ms ${conf.path + query}`,
                        );
                        const networkConnectionOK = navigator.onLine;
                        if (!verifyUser(req) && networkConnectionOK) {
                            notAuthorized(req);
                        } else if (req.status >= 200 && req.status < 300) {
                            if (conf.method === 'HEAD') {
                                callback.onResult();
                            } else {
                                const contenttype =
                                    req.getResponseHeader('Content-Type');

                                if (
                                    contenttype &&
                                    new RegExp('^application/json', 'i').test(
                                        contenttype,
                                    )
                                ) {
                                    callback.onResult(
                                        JSON.parse(req.responseText),
                                    );
                                } else {
                                    callback.onResult(req.responseText);
                                }
                            }
                        } else if (req.status >= 400 && req.status < 500) {
                            if (callback.onClientError) {
                                callback.onClientError(req);
                            }
                            if (req.status === 401) {
                                notAuthorized(req);
                            }
                        } else {
                            if (!networkConnectionOK) {
                                console.warn('Network connection broken');
                            }
                            if (callback.onError) {
                                callback.onError(req);
                            }
                        }
                        if (notifier.onStop) {
                            notifier.onStop();
                        }
                    }
                } catch (e) {
                    addHomeLogEntry(
                        `${conf.method} ${conf.path + query} threw error ${e}`,
                    );
                    console.group('Network error');
                    console.error('Error', e);
                    console.error('Request', req);
                    console.error('Callback', callback);
                    console.error('Conf', conf);
                    console.error('Outer stack', outerStack);
                    console.groupEnd();

                    throw e;
                }
            };
        })(this.requestNotifier, this.notAuthorizedHandler, this.verifyUser);

        if (this.requestNotifier.onStart) {
            this.requestNotifier.onStart();
        }

        if (conf.data) {
            req.send(conf.data);
        } else {
            req.send();
        }
        sendTime = Date.now();
    },
};

// The generated clients in @telavox/api-internal (imported as flow/api/*) send every request through
// this client.
configure(HomeClient);

export default HomeClient;
